import { useEffect, useState } from "react";
import { Wifi, TreePine } from "lucide-react";
import api from "../lib/api";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const JS_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const toMin = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const formatHM = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const hh = h % 12 || 12;
  return m === 0 ? `${hh}${period}` : `${hh}:${String(m).padStart(2,"0")}${period}`;
};

export default function Hours() {
  const [hours, setHours] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/hours").then((r) => setHours(r.data.hours)).catch(() => setHours(null));
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!hours) return null;

  const todayName = JS_DAY_NAMES[now.getDay()];
  const todayRow = hours[todayName];
  const isOpen = (() => {
    if (!todayRow || !todayRow.open || !todayRow.close) return false;
    const m = now.getHours() * 60 + now.getMinutes();
    return m >= toMin(todayRow.open) && m < toMin(todayRow.close);
  })();

  return (
    <section id="hours" data-testid="hours-section" className="py-24 sm:py-28">
      <div className="container-wide grid lg:grid-cols-[1.1fr_1fr] gap-12 items-start">
        <div className="reveal">
          <div className="eyebrow mb-3">Opening Hours</div>
          <h2 className="section-title text-4xl sm:text-5xl">When to find us</h2>
          <p className="mt-5 text-[#4A5568] text-lg leading-relaxed max-w-md">
            Drop in for a morning coffee, a lazy lunch, or an evening meal. We'd love to see you.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <div className="inline-flex items-center gap-3 bg-white border border-[#F0EAE6] rounded-2xl px-5 py-3 shadow-sm">
              <Wifi size={20} className="text-[#5979A9]" />
              <span className="text-sm text-[#2D3748]">Free Wi-Fi for customers</span>
            </div>
            <div className="inline-flex items-center gap-3 bg-white border border-[#F0EAE6] rounded-2xl px-5 py-3 shadow-sm">
              <TreePine size={20} className="text-[#5979A9]" />
              <span className="text-sm text-[#2D3748]">Covered outdoor terrace</span>
            </div>
          </div>
        </div>

        <div className="reveal bg-white rounded-3xl shadow-xl shadow-[#5979A9]/10 border border-[#F0EAE6] p-2" data-testid="hours-card">
          <div className="px-6 pt-6 pb-3 flex items-center justify-between">
            <div className="font-serif-display text-2xl text-[#5979A9]">Hours</div>
            <span data-testid="open-status-pill" className={`text-[11px] px-3 py-1.5 rounded-full uppercase tracking-[0.18em] font-bold ${isOpen ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {isOpen ? "Open Now" : "Closed Now"}
            </span>
          </div>
          <div className="px-2 pb-2">
            {DAY_ORDER.map((day) => {
              const h = hours[day];
              const isToday = day === todayName;
              const display = h && h.open && h.close ? `${formatHM(h.open)} — ${formatHM(h.close)}` : "Closed";
              return (
                <div
                  key={day}
                  data-testid={`hours-row-${day.toLowerCase()}`}
                  className={`flex items-center justify-between px-4 py-3.5 rounded-xl transition-colors ${
                    isToday ? "bg-[#FAF4F1] ring-1 ring-[#E05947]/30" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-base ${isToday ? "font-semibold text-[#5979A9]" : "text-[#2D3748]"}`}>{day}</span>
                    {isToday && (
                      <span className="text-[10px] uppercase tracking-[0.2em] bg-[#E05947] text-white px-2 py-0.5 rounded-full font-bold">
                        Today
                      </span>
                    )}
                  </div>
                  <span className={`tabular-nums text-sm ${display === "Closed" ? "text-[#E05947]/80 italic" : "text-[#4A5568]"}`}>
                    {display}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
